import {
    Button,
    Divider,
    Flex,
    MultiSelect,
    Pill,
    Popover,
    Switch,
    Text
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { FilterListOutlined } from "@mui/icons-material";
import { SvgIcon } from "@mui/material";
import { useEntityListQuery } from "@therapprove/common";
import { FC, useEffect, useRef } from "react";
import { WebsiteLeadFilterState } from "./useWebsiteLeadFilters";

// Section label style
const sectionLabelStyle = {
    fontSize: "0.7rem",
    fontWeight: 600,
    color: "var(--mantine-color-gray-6)",
    textTransform: "uppercase" as const,
    padding: "0.25rem 0.5rem 0.1rem"
};

const defaultFilters: WebsiteLeadFilterState = {
    archived: false,
    therapyCategoryIds: []
};

type WebsiteLeadFiltersProps = {
    filters: WebsiteLeadFilterState;
    setFilters: (filters: WebsiteLeadFilterState) => void;
};

export const WebsiteLeadFilterButton: FC<WebsiteLeadFiltersProps> = ({
    filters,
    setFilters
}) => {
    // Queries
    const [{ data: entityData }] = useEntityListQuery();

    // Data
    const therapyCategories =
        entityData?.therapyCategories?.therapyCategoriesList ?? [];
    const therapyCategoryOptions = therapyCategories
        .filter(Boolean)
        .map((t) => ({ value: t!.value, label: t!.label }));
    const hasActiveFilters =
        filters.archived || filters.therapyCategoryIds.length > 0;

    // State
    const [opened, { toggle, close }] = useDisclosure(false);
    const popoverRef = useRef<HTMLDivElement>(null);
    const buttonRef = useRef<HTMLButtonElement>(null);

    // Handlers
    const clearFilters = () => setFilters(defaultFilters);

    // Manual click-outside handler that ignores clicks on nested
    // portal dropdowns (e.g. the MultiSelect's combobox, which is
    // itself a Popover rendered to a portal outside this popover's
    // DOM subtree). Checking `.mantine-Popover-dropdown` covers
    // Combobox/Select/MultiSelect because they all render through
    // Popover.Dropdown; checking by class name (e.g.
    // `mantine-Combobox-dropdown`) would miss MultiSelect, which
    // uses `__staticSelector: "MultiSelect"`.
    useEffect(() => {
        if (!opened) return;

        const handleClick = (event: MouseEvent) => {
            const target = event.target as HTMLElement | null;
            if (!target) return;
            if (buttonRef.current?.contains(target)) return;
            if (popoverRef.current?.contains(target)) return;

            const nestedPopoverDropdown = target.closest(
                ".mantine-Popover-dropdown"
            );
            if (
                nestedPopoverDropdown &&
                nestedPopoverDropdown !== popoverRef.current
            )
                return;

            close();
        };

        document.addEventListener("mousedown", handleClick);
        return () => document.removeEventListener("mousedown", handleClick);
    }, [opened, close]);

    return (
        <Popover
            opened={opened}
            position="bottom-start"
            shadow="md"
            trapFocus={false}
            closeOnClickOutside={false}
        >
            <Popover.Target>
                <Button
                    ref={buttonRef}
                    variant="default"
                    leftSection={<SvgIcon component={FilterListOutlined} />}
                    onClick={toggle}
                >
                    Filters
                </Button>
            </Popover.Target>
            <Popover.Dropdown
                ref={popoverRef}
                style={{
                    width: "18rem",
                    maxHeight: "20rem",
                    overflowY: "auto",
                    padding: "0.5rem 0"
                }}
            >
                {/* Therapy Modality */}
                <Text style={sectionLabelStyle}>Therapy Modality</Text>
                <div style={{ padding: "0 0.5rem 0.25rem" }}>
                    <MultiSelect
                        variant="pill"
                        placeholder="Select modalities"
                        data={therapyCategoryOptions}
                        value={filters.therapyCategoryIds}
                        onChange={(value) =>
                            setFilters({
                                ...filters,
                                therapyCategoryIds: value
                            })
                        }
                        comboboxProps={{ withinPortal: true }}
                        searchable
                        clearable
                        size="xs"
                    />
                </div>

                {/* Archived */}
                <Divider my={4} />
                <div style={{ padding: "0.25rem 0.5rem" }}>
                    <Switch
                        checked={filters.archived}
                        onChange={(e) =>
                            setFilters({
                                ...filters,
                                archived: e.currentTarget.checked
                            })
                        }
                        label="Show archived"
                        size="sm"
                    />
                </div>

                {/* Clear all */}
                {hasActiveFilters && (
                    <>
                        <Divider my={4} />
                        <Button
                            variant="subtle"
                            size="compact-xs"
                            c="dimmed"
                            fullWidth
                            onClick={clearFilters}
                        >
                            Clear filters
                        </Button>
                    </>
                )}
            </Popover.Dropdown>
        </Popover>
    );
};

export const WebsiteLeadFilterPills: FC<WebsiteLeadFiltersProps> = ({
    filters,
    setFilters
}) => {
    // Queries
    const [{ data: entityData }] = useEntityListQuery();
    const therapyCategories =
        entityData?.therapyCategories?.therapyCategoriesList ?? [];

    // Data
    const hasActiveFilters =
        filters.archived || filters.therapyCategoryIds.length > 0;
    if (!hasActiveFilters) return null;

    const pills: { key: string; value: string; label: string }[] = [];
    for (const id of filters.therapyCategoryIds) {
        const label =
            therapyCategories.find((t) => t?.value === id)?.label ?? id;
        pills.push({ key: "therapyCategoryIds", value: id, label });
    }
    if (filters.archived) {
        pills.push({ key: "archived", value: "archived", label: "Archived" });
    }

    const removePill = (key: string, value: string) => {
        if (key === "archived") {
            setFilters({ ...filters, archived: false });
        } else if (key === "therapyCategoryIds") {
            setFilters({
                ...filters,
                therapyCategoryIds: filters.therapyCategoryIds.filter(
                    (id) => id !== value
                )
            });
        }
    };

    return (
        <Flex gap={6} align="center" wrap="wrap">
            <Pill
                size="xs"
                style={{
                    cursor: "pointer",
                    backgroundColor: "var(--mantine-color-secondary-5)",
                    color: "white"
                }}
                onClick={() => setFilters(defaultFilters)}
            >
                Clear all
            </Pill>
            {pills.map((pill) => (
                <Pill
                    key={`${pill.key}-${pill.value}`}
                    size="xs"
                    withRemoveButton
                    onRemove={() => removePill(pill.key, pill.value)}
                >
                    {pill.label}
                </Pill>
            ))}
        </Flex>
    );
};
